import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import ProductCard from "@/components/ProductCard";
import { ArrowRight } from "lucide-react";

const DEFAULT_HERO = "https://images.unsplash.com/photo-1772501387294-4be8ea1e72be?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Home() {
    const { settings } = useSite();
    const [categories, setCategories] = useState([]);
    const [bestsellers, setBestsellers] = useState([]);
    const [latest, setLatest] = useState([]);
    const [banner, setBanner] = useState(null);
    const [heroBanners, setHeroBanners] = useState([]);

    useEffect(() => {
        api.get("/categories").then(r => setCategories(r.data.filter(c => c.show_on_homepage !== false)));
        api.get("/bestsellers?limit=8").then(r => setBestsellers(r.data));
        api.get("/products?limit=8").then(r => setLatest(r.data));
        api.get("/sale/active").then(r => setBanner(r.data)).catch(() => {});
        api.get("/banners").then(r => setHeroBanners(r.data)).catch(() => setHeroBanners([]));
    }, []);

    if (!settings) return <div className="p-20 text-center font-mono">Loading…</div>;

    const featuredProducts = bestsellers.length ? bestsellers : latest;

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative border-b-2 border-black overflow-hidden">
                <div className="max-w-[1440px] mx-auto grid md:grid-cols-12 gap-0">
                    <div className="md:col-span-6 p-8 md:p-16 flex flex-col justify-between min-h-[70vh]">
                        <div className="font-mono text-[10px] uppercase tracking-widest">{settings.hero_kicker}</div>
                        <div>
                            <h1 className="font-display text-6xl md:text-8xl leading-[0.9] tracking-tighter">
                                {settings.hero_title_line1}<br />{settings.hero_title_line2}<br />
                                <span style={{ color: "hsl(var(--accent))" }}>{settings.hero_title_line3}</span>
                            </h1>
                            <p className="mt-6 max-w-md text-neutral-600">{settings.hero_subtitle}</p>
                            <div className="mt-8 flex gap-3">
                                <Link to={settings.hero_cta_link || "/shop"} data-testid="hero-shop-btn" className="inline-flex items-center gap-2 bg-black text-white px-6 py-3 border-2 border-black font-mono uppercase text-xs tracking-widest brutal-shadow">
                                    {settings.hero_cta_text} <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/about" className="inline-flex items-center px-6 py-3 border-2 border-black font-mono uppercase text-xs tracking-widest">
                                    Story
                                </Link>
                            </div>
                        </div>
                        <div className="font-mono text-[10px] uppercase text-neutral-400 tracking-widest">{settings.hero_meta}</div>
                    </div>
                    <div className="md:col-span-6 relative min-h-[50vh] md:min-h-[70vh]">
                        <img src={settings.hero_image_url || DEFAULT_HERO} alt="Hero" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            {/* HOMEPAGE BANNERS (custom slider) */}
            {heroBanners.length > 0 && (
                <section className="max-w-[1440px] mx-auto px-4 md:px-10 py-10 space-y-6">
                    {heroBanners.map(b => (
                        <Link key={b.id} to={b.button_link || "/shop"} className="relative block border-2 border-black overflow-hidden group aspect-[16/7]">
                            <img src={b.image_url} alt={b.title || ""} className="w-full h-full object-cover group-hover:scale-105 transition" />
                            {(b.title || b.subtitle) && (
                                <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/10 to-transparent flex flex-col justify-end p-6 md:p-10 text-white">
                                    {b.title && <div className="font-display text-4xl md:text-6xl tracking-tighter">{b.title}</div>}
                                    {b.subtitle && <div className="font-mono text-sm mt-2 max-w-md">{b.subtitle}</div>}
                                    {b.button_text && <span className="mt-4 inline-flex self-start bg-white text-black px-4 py-2 font-mono text-xs uppercase border-2 border-white">{b.button_text} →</span>}
                                </div>
                            )}
                        </Link>
                    ))}
                </section>
            )}

            {/* SHOP BY CATEGORY */}
            {settings.categories_section_enabled && categories.length > 0 && (
                <section className="max-w-[1440px] mx-auto px-4 md:px-10 py-16" data-testid="home-category-grid">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            {settings.categories_section_kicker && <div className="font-mono text-[10px] uppercase text-neutral-500">{settings.categories_section_kicker}</div>}
                            <h2 className="font-display text-4xl md:text-6xl tracking-tighter">{settings.categories_section_title}</h2>
                        </div>
                        <Link to="/shop" className="font-mono text-xs uppercase underline">View All</Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {categories.map((c, idx) => (
                            <Link key={c.id} to={`/shop?category=${c.slug}`} data-testid={`home-cat-${c.slug}`} className="relative aspect-[4/5] block border-2 border-black brutal-shadow overflow-hidden group bg-neutral-100">
                                {c.card_image_url ? (
                                    <img src={c.card_image_url} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <div className="font-display text-5xl md:text-6xl tracking-tighter text-neutral-300">{c.name.charAt(0)}</div>
                                    </div>
                                )}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                                <div className="absolute bottom-4 left-4 right-4 text-white">
                                    <div className="font-mono text-[10px] uppercase">Category {String(idx + 1).padStart(2, "0")}</div>
                                    <div className="font-display text-2xl md:text-3xl tracking-tighter">{c.name}</div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* SALE BLOCK */}
            {banner && (
                <section className="border-y-2 border-black text-white" style={{ background: "hsl(var(--accent))" }}>
                    <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                        <div>
                            <div className="font-mono text-[10px] uppercase">Live Sale</div>
                            <div className="font-display text-4xl md:text-5xl tracking-tighter">{banner.name}</div>
                            <div className="font-mono text-sm mt-1">{banner.headline} — up to {banner.discount_percent}% off</div>
                        </div>
                        <Link to={`/shop?sale=${banner.id}`} data-testid="home-sale-btn" className="bg-white text-black px-6 py-3 font-mono uppercase text-xs tracking-widest border-2 border-black">Shop the Sale</Link>
                    </div>
                </section>
            )}

            {/* BESTSELLERS / NEW IN */}
            {settings.bestsellers_section_enabled && featuredProducts.length > 0 && (
                <section className="max-w-[1440px] mx-auto px-4 md:px-10 py-16">
                    <div className="flex items-end justify-between mb-8">
                        <div>
                            <div className="font-mono text-[10px] uppercase text-neutral-500">{settings.bestsellers_section_kicker}</div>
                            <h2 className="font-display text-4xl md:text-6xl tracking-tighter">{settings.bestsellers_section_title}</h2>
                        </div>
                        <Link to="/shop" className="font-mono text-xs uppercase underline">All Products →</Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                        {featuredProducts.map(p => <ProductCard key={p.id} product={p} />)}
                    </div>
                </section>
            )}

            {/* MANIFESTO */}
            {settings.manifesto_enabled && (
                <section className="border-t-2 border-black bg-black text-white">
                    <div className="max-w-[1440px] mx-auto px-4 md:px-10 py-20 grid md:grid-cols-3 gap-10">
                        <div className="md:col-span-2">
                            <div className="font-mono text-[10px] uppercase text-neutral-500">{settings.manifesto_kicker}</div>
                            <h2 className="font-display text-4xl md:text-6xl tracking-tighter mt-2 whitespace-pre-line">{settings.manifesto_title}</h2>
                        </div>
                        <div className="space-y-6 text-sm text-neutral-300">
                            {[1, 2, 3].map(n => (
                                <div key={n}>
                                    <div className="font-mono text-xs text-white uppercase">{settings[`manifesto_col${n}_title`]}</div>
                                    {settings[`manifesto_col${n}_body`]}
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
